import { Flex, Input, TableProps, Tag, Button, Modal, Form, InputNumber, Tabs, DatePicker, Select, App, notification } from "antd";
import { FC, useEffect, useRef, useState, useContext, useMemo } from "react";
import React from "react";
import { useTranslation } from 'react-i18next';
import { ChangeEnum, Order, OperateEnum, ConfirmedEnum } from "@/types/inventory";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import dayjs, { Dayjs } from "dayjs";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { orderListV2, orderFetch as v2OrderFetch, orderItemCountUpdates, orderArrivalAtUpdate, orderPreOut } from "@/apis/inventoryv2";
import PrintQC, { PrintType } from "@/SupplierPages/Home/InventoryOrder/components/PrintQC";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { dateFormat } from "@/utils/format";
import { DictContext } from "@/layout/context/DictContext";
import { OrderContent } from "./styles";
import { orderListUnconfirmed } from "@/apis/inventory";
import { IQCOrder, OrderV2, QCStateEnum } from "@/types/inventoryV2";
import SText from "@/styles/SText";
import AddIQC from "../IQCList/components/AddIQC";
import useIqcEnums from "../IQCList/hooks/useIqcEnums";
import useOrderPreOutLocationTypes from "@/hooks/useOrderPreOutLocationTypes";
import useSourceType from "@/hooks/useSourceType";
import useDataList from "@/hooks/useDataList";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import WMSAvatar from "@/styles/WMSAvatar";
import errorCodes from "@/apis/error";

enum TabEnum {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

interface OrderManageFilters {
  keyword: string;
  orderType: ChangeEnum | undefined;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  confirmed: ConfirmedEnum | undefined;
}

const INITIAL_ORDER_MANAGE_FILTERS: OrderManageFilters = {
  keyword: '',
  orderType: undefined,
  startTime: null,
  endTime: null,
  confirmed: undefined,
};

const PREPARATION_INBOUND_TYPES: ChangeEnum[] = [
  ChangeEnum.ORDER_IN,
  ChangeEnum.PRE_IN,
  ChangeEnum.IN,
];

const PREPARATION_OUTBOUND_TYPES: ChangeEnum[] = [
  ChangeEnum.PRE_OUT,
  ChangeEnum.OUT,
  ChangeEnum.SALES_OUT,
];

const OrderManage: FC = () => {
  const { t } = useTranslation();
  const { getQCState } = useIqcEnums();
  const { changeTypes, getChangeType, getOpType } = useSourceType();
  const { RangePicker } = DatePicker;
  // const { id } = useWarehouseParams();
  const [id, setId] = useState<string>('')
  const tableRef = useRef<TableLayoutRef | null>(null);
  const curOrg = useSelector((state: RootState) => state.orgReducer.curOrg)
  const [openDetailId, setOpenDetailId] = useState<string>("");
  const [order, setOrder] = useState<Order | null>(null)
  const [orders, total, listLoading, { fetchList, setList }] = useDataList<
    OrderV2,
    Parameters<typeof orderListV2>
  >({ fetchApi: orderListV2 });
  // 由 Hook 中的原始订单派生旧版展示字段，不重复维护列表状态。
  const list = useMemo<Order[]>(() => orders.map(v2Order => ({
    id: v2Order.id,
    orgId: curOrg?.id,
    number: v2Order.number,
    changeType: v2Order.type,
    opType: v2Order.opType,
    sourceType: 1,
    sourceId: v2Order.logBookId,
    confirmed: v2Order.confirmState,
    createdAt: v2Order.createdAt,
    updatedAt: v2Order.updatedAt,
    creator: v2Order.creator,
    orderType: v2Order.type,
    arrivalAt: v2Order.arrivalAt,
    preTime: v2Order.preTime,
    items: v2Order.items || [],
    state: v2Order.state,
    source: '',
    scans: [],
    returnOrderId: v2Order.returnOrderId,
    iqcState: v2Order.iqcState,
  } as unknown as Order)), [orders, curOrg?.id]);
  const [loading, setLoading] = useState(false)
  const { rootCategories } = useContext(DictContext)
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState<TabEnum>(TabEnum.INBOUND);
  const [isMounted, setIsMounted] = useState(false);
  const [printingOrders, setPrintingOrders] = useState<Set<string>>(new Set());
  const [printOrderData, setPrintOrderData] = useState<any>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [openIqc, setOpenIqc] = useState<OrderV2 | null>(null);

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<OrderManageFilters>(INITIAL_ORDER_MANAGE_FILTERS);
  const { keyword, orderType, startTime, endTime, confirmed } = draftFilters;


  const { modal } = App.useApp();
  const selectPreOutLocationTypes = useOrderPreOutLocationTypes();

  // 组件挂载后设置标志
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // 自动触发打印
  useEffect(() => {
    if (printOrderData) {
      // 延迟一下确保组件已完全渲染
      setTimeout(() => {
        // 查找隐藏的打印容器中的按钮
        const printContainer = document.getElementById('iqc-print-container');
        if (printContainer) {
          const printButton = printContainer.querySelector('button') as HTMLButtonElement;
          if (printButton) {
            printButton.click();
          }
        }
      }, 300);
    }
  }, [printOrderData]);

  const options = rootCategories.map(item => {
    return { label: item.name, value: item.id }
  })
  options.unshift({ label: t("category.all"), value: '' })

  useEffect(() => {
    if (isMounted && curOrg) {
      const nextFilters = replaceFilters(INITIAL_ORDER_MANAGE_FILTERS);
      tableRef?.current?.resetTablePage();
      fetch(0, nextFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, isMounted, replaceFilters]);

  const handleTabChange = (key: string) => {
    setActiveTab(key as TabEnum);
  };

  const tableTabs = [
    {
      key: TabEnum.INBOUND,
      label: t('warehouse.inbound'),
    },
    {
      key: TabEnum.OUTBOUND,
      label: t('warehouse.outbound'),
    },
  ];

  const handleExportAll = async (): Promise<Order[]> => {
    try {
      setExportLoading(true);
      setExportProgress(0);
      const allOrderData: Order[] = [];
      const batchSize = 100;
      let offset = 0;
      let hasMore = true;
      // 根据 tab 确定要查询的类型列表
      // 如果用户选择了特定类型，则只查询该类型；否则查询该 tab 下的所有类型
      let types: number[];
      if (appliedFilters.orderType !== undefined) {
        types = [appliedFilters.orderType];
      } else {
        types = activeTab === TabEnum.INBOUND
          ? PREPARATION_INBOUND_TYPES
          : PREPARATION_OUTBOUND_TYPES;
      }

      while (hasMore && curOrg) {
        const response = await orderListV2({
          orgId: curOrg.id,
          keyword: appliedFilters.keyword,
          params: {
            body: appliedFilters.keyword,
            ints: types,  // 传递多个类型
            pagination: { offset, take: batchSize },
            startTime: appliedFilters.startTime ? appliedFilters.startTime.startOf('day').valueOf() : undefined,
            endTime: appliedFilters.endTime ? appliedFilters.endTime.endOf('day').valueOf() : undefined,
          },
          confirmed: appliedFilters.confirmed
        });
        // 将 OrderV2 转换为 Order 类型
        const orders: Order[] = (response.body || []).map((v2Order: any) => ({
          id: v2Order.id,
          orgId: curOrg.id,
          number: v2Order.number,
          changeType: v2Order.type as unknown as ChangeEnum,
          opType: v2Order.opType as unknown as OperateEnum,
          sourceType: 1,
          sourceId: v2Order.logBookId,
          confirmed: v2Order.confirmState,
          createdAt: v2Order.createdAt,
          updatedAt: v2Order.updatedAt,
          creator: v2Order.creator,
          orderType: v2Order.type,
          arrivalAt: v2Order.arrivalAt,
          preTime: v2Order.preTime,
          items: v2Order.items || [],
          state: v2Order.state,
          source: '',
          scans: [],
          returnOrderId: v2Order.returnOrderId,
        } as unknown as Order));

        if (response.body && response.body.length > 0) {
          allOrderData.push(...orders);
          offset += batchSize;
          const progress = Math.min(
            Math.round(
              (allOrderData.length /
                (response.pagination?.size || allOrderData.length)) *
              100
            ),
            99
          );
          setExportProgress(progress);
        } else {
          hasMore = false;
        }

        if ((response?.body?.length || 0) < batchSize) {
          hasMore = false;
        }
      }

      setExportProgress(100);
      return allOrderData;
    } catch (error) {
      notification.error({
        placement: 'top',
        message: t("common.error"),
        description: t("common.exportErr")
      });
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  // 根据 tab 筛选操作类型选项
  const filteredChangeTypes = useMemo(() => {
    if (activeTab === TabEnum.INBOUND) {
      // 入库类型
      return changeTypes.filter(item =>
        PREPARATION_INBOUND_TYPES.includes(item.value)
      );
    } else {
      // 出库类型
      return changeTypes.filter(item =>
        PREPARATION_OUTBOUND_TYPES.includes(item.value)
      );
    }
  }, [activeTab]);

  const orderColumns = useMemo(() => [
    { key: 'number', label: activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber') },
    { key: 'creator', label: t('order.preparedBy') },
    { key: 'createdAt', label: t('order.preparationTime') },
    { key: 'preTime', label: t('warehouse.requiredArrivalAt') },
    { key: 'arrivalAt', label: t('warehouse.actualArrivalAt') },
    { key: 'changeType', label: t('order.oprtType') },
    { key: 'opType', label: t('order.operationSource') },
    { key: 'confirmed', label: t('order.operationStatus') },
  ], [activeTab, t]);

  const orderDataMapper = (item: Order, t: any) => {
    const changeTypeInfo = getChangeType(item.changeType);
    const opTypeInfo = getOpType(item.opType);

    // 根据tab和确认状态显示不同的状态文字
    let statusLabel = '';
    if (activeTab === TabEnum.INBOUND) {
      statusLabel = item.confirmed === 0 ? t('order.inTransit') : t('order.inboundCompleted');
    } else {
      statusLabel = item.confirmed === 0 ? t('order.preparing') : t('order.outboundCompleted');
    }

    const numberLabel = activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber');

    return {
      [numberLabel]: item.number || '',
      [t('order.preparedBy')]: getUserName(item.creator) || '',
      [t('order.preparationTime')]: item.createdAt ? dateFormat(dayjs(item.createdAt)) : '',
      [t('warehouse.requiredArrivalAt')]: item.preTime ? dateFormat(dayjs(item.preTime)) : '',
      [t('order.oprtType')]: changeTypeInfo?.label || '',
      [t('order.operationSource')]: opTypeInfo?.label || '',
      [t('order.operationStatus')]: statusLabel,
    };
  };

  const handleList = (id: string, date: number) => {
    setList(
      last => last.map(data => ({
        ...data,
        arrivalAt: data.id === id ? date : data.arrivalAt
      }))

    )
  }

  const handleSign = (id: string) => {
    let date = Date.now();
    let loading = false;
    modal.confirm({
      title: t('warehouse.plSignDate'),
      okButtonProps: { loading },
      content: (
        <DatePicker defaultValue={dayjs()} onChange={val => date = val?.valueOf()} />
      ),
      onOk() {
        loading = true;
        orderArrivalAtUpdate(id, date)
          .then(() => {
            notification.success({ message: t('warehouse.signSucc') })
            handleList(id, date)
          })
          .catch(() => {
            notification.error({ message: t('warehouse.signFail') })
          })
          .finally(() => loading = false)
      },
    })
  }

  // 根据 tab 动态生成表格列
  const columns = useMemo((): TableProps<Order>["columns"] => {
    const baseColumns: TableProps<Order>["columns"] = [
      {
        title: activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber'),
        dataIndex: "number",
        key: "number",
        width: 200,
        ellipsis: true,
      },
      {
        title: <SText text={t("order.preparedBy")} />,
        dataIndex: "creator",
        width: 100,
        render: (_, item) => {
          let userData = item.creator;
          return (
            userData && <Flex>
              <div>
                <WMSAvatar creator={userData} uniChar={userData.id}
                  avatarId={userData.avatar}
                  size={24} alt={getUserAvatarTip(userData)}
                />
              </div>
              <SText text={getUserName(userData)} />
            </Flex>
          )
        },
      },
      {
        title: t('order.preparationTime'),
        dataIndex: "createdAt",
        key: "createdAt",
        width: 100,
        render: (createdAt) => {
          const fullTime = dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss');
          return React.createElement('span', { title: fullTime }, dateFormat(dayjs(createdAt)));
        },
      },
      {
        title: t('warehouse.requiredArrivalAt'),
        dataIndex: "preTime",
        key: "preTime",
        width: 100,
        render: (preTime) => preTime && dateFormat(dayjs(preTime)),
      },
      {
        title: t('warehouse.actualArrivalAt'),
        dataIndex: "arrivalAt",
        key: "arrivalAt",
        width: 100,
        render: (arrivalAt) => arrivalAt && dateFormat(dayjs(arrivalAt)),
      },
      {
        title: t('order.oprtType'),
        key: "changeType",
        width: 120,
        dataIndex: "changeType",
        render: (_, { changeType, id }) => {
          let info = getChangeType(changeType);
          return (
            <SText tagMode color={info?.color} text={info?.label ?? ""} />
          );
        },
      },
      {
        title: t('order.operationSource'),
        key: "opType",
        width: 80,
        dataIndex: "opType",
        render: (_, { opType, id }) => {
          let info = getOpType(opType);
          return (
            <SText tagMode color={info?.color} text={info?.label ?? ""} />
          );
        },
      },
      {
        title: t('order.operationStatus'),
        key: "confirmed",
        width: 80,
        dataIndex: "confirmed",
        render: (_, { confirmed, id, arrivalAt }) => {
          // 根据tab显示不同的状态文字
          if (activeTab === TabEnum.INBOUND) {
            // 入库：0-在途，1-已入库
            const label = confirmed === ConfirmedEnum.NOTCONFIRM ? (!!arrivalAt ? t('warehouse.signed') : t('order.inTransit')) : t('order.inboundCompleted');
            const color = confirmed === ConfirmedEnum.NOTCONFIRM ? 'orange' : 'green';
            return <SText tagMode color={color} text={label ?? ""} />
          } else {
            // 出库：0-备料，1-已出库
            const label = confirmed === 0 ? t('order.preparing') : t('order.outboundCompleted');
            const color = confirmed === 0 ? 'orange' : 'green';
            return <SText tagMode color={color} text={label ?? ""} />
          }
        },
      },
      {
        title: t('warehouse.iqcState'),
        key: "iqcState",
        width: 100,
        dataIndex: "iqcState",
        render: (_, record) => {
          let label = getQCState((record as unknown as OrderV2).iqcState);
          return (
            label && <SText tagMode text={label.label} color={label.color} />
          )
        }
      },
      {
        title: t('order.quantityConfirm'),
        key: "editOrder",
        width: 100,
        dataIndex: "editOrder",
        render: (_, { id }) => {
          return (
            <Button type="primary" onClick={() => {
              setOpenDetailId(id)
              v2OrderFetch(id).then(res => {
                setOrder(res as unknown as Order)
                // 重置表单并设置初始值
                form.resetFields()
                const quantities: Record<string, number> = {}
                res.items?.forEach((item: any, index: number) => {
                  quantities[index] = item.count || item.quantity || 0
                })
                form.setFieldsValue({ quantities })
              })
            }}>
              {t('order.quantityConfirm')}
            </Button>
          );
        },
      },
      {
        title: t('warehouse.iqcCheck'),
        key: "iqcCheck",
        width: 100,
        render: (_, record) => <Button onClick={() => setOpenIqc(record as unknown as OrderV2)}>{t('warehouse.iqcCheck')}</Button>
      },
    ];

    // 只在入库tab添加IQC打印列
    if (activeTab === TabEnum.INBOUND) {
      baseColumns.push({
        title: 'IQC打印',
        key: "iqcPrint",
        width: 100,
        dataIndex: "iqcPrint",
        render: (_: any, record: Order) => {
          const isPrinting = printingOrders.has(record.id);

          const handlePrint = async () => {
            setPrintingOrders(prev => new Set(prev).add(record.id));

            try {
              // 先获取订单详情
              const orderDetail = await v2OrderFetch(record.id);

              if (!orderDetail.items || orderDetail.items.length === 0) {
                notification.warning({
                  placement: 'top',
                  message: '无订单项',
                  description: '该订单没有可打印的项目'
                });
                setPrintingOrders(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(record.id);
                  return newSet;
                });
                return;
              }

              // 设置打印数据，触发 PrintQC 组件执行打印
              setPrintOrderData(orderDetail);

            } catch (error) {
              console.error('获取订单详情失败:', error);
              notification.error({
                placement: 'top',
                message: '获取订单详情失败',
                description: '无法进行IQC打印'
              });
              setPrintingOrders(prev => {
                const newSet = new Set(prev);
                newSet.delete(record.id);
                return newSet;
              });
            }
          };

          // 显示打印按钮
          return (
            <Button
              type="primary"
              onClick={handlePrint}
              loading={isPrinting}
              disabled={isPrinting}
            >
              {isPrinting ? '打印中...' : 'IQC打印'}
            </Button>
          );
        },
      },
        {
          title: t('warehouse.sign'),
          key: "sign",
          width: 100,
          dataIndex: "sign",
          render(_, record: any) {
            return !!!record.arrivalAt && <Button onClick={() => handleSign(record.id)}>
              {t('warehouse.sign')}
            </Button>
          },
        }
      );
    }

    return baseColumns;
  }, [activeTab, t, form, printingOrders, notification]);


  const headerChildren = (
    <div className="header-wrap" style={{ width: "100%" }}>
      <Flex vertical >
        {/* 第一行：Tab 和筛选条件 */}
        <Flex justify="space-between" align="center">
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={tableTabs}
            type="card"
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <ListFilterBar
              onSearch={() => handleSearch()}
              onReset={() => handleClearFilters()}
              disabled={listLoading}
            >
              {({ submit }) => (
                <>
                  <Select style={{ minWidth: 160 }} value={confirmed} placeholder={t('warehouse.plconfed')}
                    options={[
                      { label: t('warehouse.noconf'), value: ConfirmedEnum.NOTCONFIRM },
                      { label: t('warehouse.confed'), value: ConfirmedEnum.CONFIRM },
                    ]}
                    onChange={val => updateDraftFilters({ confirmed: val })} allowClear
                  />
                  <RangePicker
                    value={[startTime, endTime]}
                    onChange={val => updateDraftFilters({
                      startTime: val?.[0] ?? null,
                      endTime: val?.[1] ?? null,
                    })}
                    placeholder={[t('order.startTime'), t('order.endTime')]}
                    style={{ width: 280 }}
                  />
                  <Select
                    allowClear
                    placeholder={t('order.selectChangeTypePl')}
                    value={orderType}
                    onChange={value => updateDraftFilters({ orderType: value })}
                    options={filteredChangeTypes}
                    style={{ width: 160 }}
                  />
                  <Input
                    style={{ width: 200 }}
                    value={keyword}
                    onChange={event => updateDraftFilters({ keyword: event.target.value })}
                    onPressEnter={submit}
                    placeholder={t('order.searchByOrderNumber')}
                  />
                </>
              )}
            </ListFilterBar>
          </div>
        </Flex>
        {/* 第二行：导出按钮 */}
        <Flex gap={10}>
          <ExportExcelButton
            list={list}
            columns={orderColumns}
            defaultSelectedColumns={[
              activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber'),
              t('order.preparedBy'),
              t('order.preparationTime')
            ]}
            dataMapper={orderDataMapper}
            fileName="订单列表.xlsx"
          />
          <ExportExcelButton
            isAll
            loadData={handleExportAll}
            columns={orderColumns}
            defaultSelectedColumns={[
              activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber'),
              t('order.preparedBy'),
              t('order.preparationTime')
            ]}
            dataMapper={orderDataMapper}
            fileName="订单列表_全部.xlsx"
          />
        </Flex>
      </Flex>
    </div>
  );

  const fetch = (offset: number, filters: OrderManageFilters) => {
    if (!curOrg) {
      console.warn('curOrg is not available');
      return;
    }

    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;

    // 根据 tab 确定要查询的类型列表
    // 如果用户选择了特定类型，则只查询该类型；否则查询该 tab 下的所有类型
    let types: number[];
    if (filters.orderType !== undefined) {
      types = [filters.orderType];
    } else {
      types = activeTab === TabEnum.INBOUND
        ? PREPARATION_INBOUND_TYPES
        : PREPARATION_OUTBOUND_TYPES;
    }

    // 使用 V2 API，通过 ints 参数传递多个类型
    fetchList({
      orgId: curOrg.id,
      keyword: filters.keyword,
      params: {
        body: filters.keyword,
        ints: types,  // 传递多个类型
        pagination: { offset, take },
        startTime: filters.startTime ? filters.startTime.startOf('day').valueOf() : undefined,
        endTime: filters.endTime ? filters.endTime.endOf('day').valueOf() : undefined,
      },
      confirmed: filters.confirmed,
    })
      .catch((err: any) => {
        console.error('Fetch error:', err);
      });
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_ORDER_MANAGE_FILTERS);
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  // 检查订单是否已确认（已入库或已出库）- 使用 confirmState
  // Order 类型使用 confirmed，OrderV2 类型使用 confirmState
  const isOrderConfirmed = (order as any)?.confirmState === 1 || (order as any)?.confirmed === 1;

  const updateQty = async () => {
    setLoading(true);

    let values: AnyObject = {};
    try { values = await form.validateFields(); }
    catch (error) { setLoading(false); }

    // 收集需要更新的订单项
    const updateItems: Array<{ orderItemId: string, count: number }> = [];
    const rollBackItems: Array<{ orderItemId: string, count: number }> = [];
    if (values && order) {
      order?.items?.forEach((item: any, index: number) => {
        const newQuantity = values.quantities?.[index]
        if (newQuantity !== undefined && newQuantity !== item.count && newQuantity !== item.quantity) {
          updateItems.push({
            orderItemId: item.id,
            count: newQuantity
          });
          rollBackItems.push({
            orderItemId: item.id,
            count: item.count
          });
        }
      });
    }

    try {
      // 如果有需要更新的项，使用批量更新接口
      if (updateItems.length > 0) {
        const needsPreOut = [ChangeEnum.OUT, ChangeEnum.PRE_OUT].includes((order as unknown as OrderV2).type);
        const locationTypes = needsPreOut ? await selectPreOutLocationTypes() : undefined;
        if (needsPreOut && !locationTypes) return;
        await orderItemCountUpdates(updateItems);
        //出库需要preout
        if (needsPreOut && locationTypes) {
          await orderPreOut(order?.id ?? '', locationTypes);
        }
        notification.success({ placement: 'top', message: t('order.updateOrderSuccess') })
        setOpenDetailId("")
        fetch(tableRef.current?.offset || 0, appliedFilters)
      } else {
        // 没有需要更新的项
        notification.info({ placement: 'top', message: t('order.noChanges') })
        setOpenDetailId("")
      }
    } catch (err: any) {
      if (err?.code === 73007 || err?.code === 73008) {
        if (Array.isArray(err?.extra)) {
          let errCodes = (order as unknown as OrderV2).items
            .filter(item => err.extra.includes(item.productId) || err.extra.includes(item.product?.code))
            .map(item => item.product?.code ?? '')
            .join(', ')
          notification.error({
            placement: 'top',
            message: t('order.noChanges'),
            description: t('apiError.error31') + `: ${errCodes}`,
          });
        } else notification.error({
          placement: 'top',
          message: t('order.noChanges'),
          description: t('warehouse.insufficientInventory')
        });
      } else if (err?.code === 73010) {
        notification.error({
          message: t('inventory.proQtyFalse'),
          description: err?.extra?.map((i: { productId: string }) => i.productId)?.join(', ')
        })
      } else if ([73022, 73014].includes(err)) {
        notification.error({
          message: t('inventory.calcErr')
        })
      } else if ([73024, 73028].includes(err.code)) {
        notification.error({
          message: t('warehouse.insufficientInventory'),
          description: err?.extra?.productCode
        })
      } else {
        errorCodes[err] && notification.error({ message: errorCodes[err] })
      }
      // orderPreOut 失败，使用预先定义的 rollBackItems 回滚
      await orderItemCountUpdates(rollBackItems);
    } finally { setLoading(false) }

  }

  const handleClose = (qcOrder: IQCOrder | null) => {
    if (!!qcOrder && openIqc?.iqcState === null) setList(last => last.map(item => ({ ...item, iqcState: item.id === openIqc.id ? QCStateEnum.QC_ING : (item as any).iqcState })))
    setOpenIqc(null)
  };
  const handleQCState = (val: QCStateEnum, orderId: string) => {
    setList(last => last.map(item => ({ ...item, iqcState: item.id === orderId ? val : (item as any).iqcState })))
  }

  const btnChildren = (
    <div>
      <Button style={{ marginRight: 10 }} loading={loading} onClick={() => {
        setOpenDetailId("")
      }}>
        {t('order.cancel')}
      </Button>
      <Button
        loading={loading}
        type="primary"
        disabled={isOrderConfirmed}
        onClick={updateQty}>
        {t('order.confirm')}
      </Button>
    </div>

  )

  return (

    <Flex vertical style={{ width: '100%', height: '100%' }}>
      <OrderContent>
        <STableLayout
          loading={listLoading}
          dataSource={list}
          columns={columns as ColumnsType<AnyObject>}
          headerChildren={headerChildren}
          tableLayoutRef={tableRef}
          total={total}
          handleChange={(offset) => {
            // 后端分页，根据offset请求对应页的数据
            fetch(offset, appliedFilters);
          }}
        // onRow={(item) => {
        //   return {
        //     onClick: () => setOpenDetailId(item.id),
        //   };
        // }}
        />
      </OrderContent>
      {/* <OrderDetail
          id={openDetailId}
          open={!!openDetailId}
          handleRm={rmItem}
          handleCancel={(status) => {
            if (status && openDetailId) {
              const detail = list.find(item => item.id === openDetailId)
              if (detail) {
                updateItem({ ...detail, confirmed: status })
              }
            }
            setOpenDetailId("")
          }}
        /> */}
      <Modal
        open={!!openDetailId}
        onCancel={() => setOpenDetailId("")}
        title={
          <div>
            {t('order.quantityConfirm')}
            {isOrderConfirmed && (
              <Tag color="green" style={{ marginLeft: 10 }}>
                {activeTab === TabEnum.INBOUND ? t('order.inboundCompleted') : t('order.outboundCompleted')}
              </Tag>
            )}
          </div>
        }
        footer={btnChildren}
        destroyOnClose={true}
        width={1100}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical">
          <div style={{
            maxHeight: 'calc(100vh - 300px)',
            overflowY: 'auto',
            paddingRight: '8px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '16px'
            }}>
              {order?.items?.map((item: any, index: number) => (
                <div key={item.id} style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  padding: '12px',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{
                      fontWeight: 'bold',
                      marginBottom: '6px',
                      fontSize: '13px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={`${item.product?.code || '-'} - ${item.product?.name || '-'}`}>
                      {item.product?.code || '-'} - {item.product?.name || '-'}
                    </div>
                    <div style={{
                      color: '#666',
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={`${t('product.model')}: ${item.product?.model || '-'}`}>
                      {t('product.model')}: {item.product?.model || '-'}
                    </div>
                  </div>
                  <Form.Item
                    name={['quantities', index]}
                    label={t('order.quantity')}
                    rules={[{ required: true, message: t('order.quantity') }]}
                    initialValue={item.count || item.quantity}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber changeOnWheel
                      style={{ width: '100%' }}
                      min={1}
                      size="small"
                      disabled={isOrderConfirmed}
                    />
                  </Form.Item>
                </div>
              ))}
            </div>
          </div>
        </Form>
      </Modal>
      {/* 隐藏的打印组件 - 在后台执行打印 */}
      {printOrderData && (
        <div id="iqc-print-container" style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}>
          <PrintQC
            selectedRows={printOrderData.items ? printOrderData.items.map((itm: any) => ({ ...itm, order: { ...printOrderData, items: [] } })) : []}
            disabled={false}
            onStart={() => {
            }}
            onEnd={() => {
              // 清理打印状态
              setPrintOrderData(null);
              setPrintingOrders(prev => {
                const newSet = new Set(prev);
                newSet.delete(printOrderData.id);
                return newSet;
              });
            }}
            printType={PrintType.SUP}
          />
        </div>
      )}
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
      {!!openIqc && <AddIQC order={openIqc} open={!!openIqc} onClose={handleClose} handleQCState={handleQCState} />}

    </Flex>
  );
};

export default OrderManage;
